/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React from 'react';
import { Empty, Space, Tooltip } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { ContextualLearningInfo } from '@/generated/graphql';
import MarkdownRenderer from '../../MarkdownRenderer';
import AppUtil from '../../../utils/AppUtil';
import styles from './ContextualLearningPreview.module.less';
import { useAppDispatch } from '../../../stores/hooks';
import { showPopup } from '../../../stores/slices/appPopup';
import AppConstants from '../../../utils/AppConstants';
import { getDocumentUrlFromContextMetadata } from '../../../utils/exception';

interface IContextualLearningPreviewProps {
  contextualLearning: ContextualLearningInfo | null;
  methodText: string;
  onEditClick?: () => void;
}

function ContextualLearningPreview(props: IContextualLearningPreviewProps) {
  const { contextualLearning, methodText, onEditClick } = props;

  const dispatch = useAppDispatch();

  const s3Url = getDocumentUrlFromContextMetadata(
    contextualLearning?.contextMetadata || null
  );
  const s3Parts = AppUtil.getS3URIParts(s3Url);

  const handleViewDetails = () => {
    dispatch(
      showPopup({
        popupId: AppConstants.POPUPS.VIEW_S3_FILE,
        popupParams: {
          title: s3Parts?.filename,
          s3Object: {
            bucket: s3Parts?.bucket,
            key: s3Parts?.key
          }
        }
      })
    );
  };

  return (
    <div className={styles.generated} data-cy="cbl-generatedpreview">
      <div className={styles.generatedHeader}>Learning</div>

      <div className={styles.generatedContent}>
        {s3Parts ? (
          <div className={styles.generatedContext}>
            <div className={styles.generatedContextHeader}>
              <span className="first-child">Context</span>
              <span className="second-child">auto-generated</span>
            </div>
            <div
              onClick={handleViewDetails}
              className={styles.generatedContextFilname}
            >
              {s3Parts.filename}
            </div>
            {/* TODO: V2 */}
            {/* <div className={styles.generatedContextImage} /> */}
          </div>
        ) : (
          <Empty description="No context found" />
        )}

        <div className={styles.generatedMethod}>
          <div className={styles.generatedMethodHeader}>
            <div className="first-child">Method</div>
            <Space>
              <Tooltip title="Edit this method">
                <EditOutlined onClick={onEditClick} />
              </Tooltip>
              {/* TODO: V2 */}
              {/* <Tooltip title="Delete this method">
                <DeleteOutlined onClick={onDeleteClick} />
              </Tooltip> */}
            </Space>
          </div>

          <div
            className={styles.generatedMethodText}
            data-cy="cbl-generated-method"
          >
            <MarkdownRenderer>{methodText}</MarkdownRenderer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContextualLearningPreview;
